// Marketing.tsx — admin page for email campaigns (Resend Broadcasts)
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CampaignEditor from './components/CampaignEditor';
import { marketingApi, type CampaignListItem, type CampaignStatus, type MarketingOverview } from './marketingApi';

const STATUS_STYLE: Record<CampaignStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-amber-100 text-amber-800',
  sent: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

const Marketing: React.FC = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [overview, setOverview] = useState<MarketingOverview | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // undefined = list view, null = new campaign, number = editing
  const [editingId, setEditingId] = useState<number | null | undefined>(undefined);

  useEffect(() => {
    if (!isAdmin) window.location.href = '/';
  }, [isAdmin]);

  const refresh = useCallback(async () => {
    try {
      const [ov, list] = await Promise.all([marketingApi.overview(), marketingApi.list()]);
      setOverview(ov);
      setCampaigns(list);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin, refresh]);

  if (!isAdmin) return null;

  const duplicate = async (id: number) => {
    try {
      const copy = await marketingApi.duplicate(id);
      await refresh();
      setEditingId(copy.id);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Duplicate failed');
    }
  };

  const remove = async (c: CampaignListItem) => {
    if (!window.confirm(`Delete draft "${c.name}"?`)) return;
    try {
      await marketingApi.remove(c.id);
      refresh();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header — same pattern as Game Management */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
                <p className="text-gray-600 mt-1">Send news and event emails to subscribed users</p>
              </div>
            </div>
            <div className="hidden sm:flex items-center space-x-2">
              <span className="text-sm text-green-600 font-medium">Admin Access</span>
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        )}
        {overview && !overview.configured && (
          <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            RESEND_MARKETING_API_KEY or RESEND_SUBSCRIBERS_SEGMENT_ID is missing on the server. You can write drafts and
            send tests, but not send to subscribers.
          </div>
        )}

        {loading || !overview ? (
          <div className="py-20 text-center text-gray-500">Loading…</div>
        ) : editingId !== undefined ? (
          <CampaignEditor
            key={editingId ?? 'new'}
            campaignId={editingId}
            overview={overview}
            onBack={() => {
              setEditingId(undefined);
              refresh();
            }}
            onChanged={refresh}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <p className="text-sm text-gray-500">Subscribers</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{overview.subscriber_count}</p>
                <p className="text-xs text-gray-500 mt-1">Users who opted in</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <p className="text-sm text-gray-500">Sent from</p>
                <p className="text-sm font-medium text-gray-900 mt-2 break-all">{overview.from_email}</p>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center justify-center">
                <button
                  onClick={() => setEditingId(null)}
                  className="w-full px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  + New campaign
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {campaigns.length === 0 ? (
                <div className="py-16 text-center text-gray-500 text-sm">No campaigns yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        <th className="px-6 py-3">Campaign</th>
                        <th className="px-6 py-3">Status</th>
                        <th className="px-6 py-3">Recipients</th>
                        <th className="px-6 py-3">Last updated</th>
                        <th className="px-6 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {campaigns.map((c) => (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <button onClick={() => setEditingId(c.id)} className="text-left">
                              <p className="font-medium text-gray-900 hover:text-blue-600">{c.name}</p>
                              <p className="text-gray-500 truncate max-w-md">{c.subject || '(no subject)'}</p>
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[c.status]}`}>
                              {c.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-700">{c.recipient_count ?? '—'}</td>
                          <td className="px-6 py-4 text-gray-500 whitespace-nowrap">
                            {new Date(c.updated_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-right whitespace-nowrap space-x-3">
                            <button onClick={() => setEditingId(c.id)} className="text-blue-600 hover:text-blue-800">
                              {c.status === 'draft' ? 'Edit' : 'View'}
                            </button>
                            <button onClick={() => duplicate(c.id)} className="text-gray-600 hover:text-gray-900">
                              Duplicate
                            </button>
                            {c.status === 'draft' && (
                              <button onClick={() => remove(c)} className="text-red-600 hover:text-red-800">
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Marketing;
